<template>
  <div class="flex items-center mb-0! gap-1">
    <div
      v-if="show"
      :class="badgeContainerClass"
    >
      <div class="flex items-center gap-1">
        <span :class="labelClass">{{ labelText }}</span>
        <span class="text-sm">
          <span :class="['font-semibold', regra_colorizacao_progress_bar]">{{ displayUsed }}</span>
          <span :class="[regra_colorizacao_progress_bar]">/</span>
          <span :class="[regra_colorizacao_progress_bar]">{{ displayLimit }}</span>
          <span v-if="mode === 'minimalista'" :class="[regra_colorizacao_progress_bar]"> ({{ percent + '%' }})</span>
        </span>
      </div>

      <div :class="progressTrackClass">
        <div
          v-if="mode === 'normal'"
          :style="{ width: percent + '%' }"
          :class="['h-full transition-all duration-300', progressFillClass]"
        />
      </div>
      <PVButton
        v-if="mode === 'normal'"
        icon="pi pi-question-circle"
        :class="[helpIconClass, 'cursor-pointer', 'info-icon-button']"
        text
        rounded
        v-pv-tooltip="'Franquia referente à ' + target_desc"
        @click="() => {}"
      />
    </div>
    <PVButton
      v-if="mode === 'minimalista'"
      icon="pi pi-question-circle"
      :class="[helpIconClass, 'cursor-pointer', 'info-icon-button']"
      text
      rounded
      v-pv-tooltip="'Franquia referente à ' + target_desc"
      @click="() => {}"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ModulosEnum } from '@/enums/ModulosEnum'

const props = defineProps<{
  module: string | string[]
  mode?: 'normal' | 'minimalista' | 'avancado'
  counter: number
  max: number
}>()

const mode = computed(() => props.mode || 'normal')

const badgeContainerClass = computed(() => {
  return [
    'select-none inline-flex items-center gap-2 campaign-badge',
    {
      'campaign-badge--minimalista': mode.value === 'minimalista',
      'campaign-badge--normal': mode.value === 'normal',
      'campaign-badge--avancado': mode.value === 'avancado',
    },
  ]
})

const labelClass = computed(() => 'text-xs font-semibold campaign-label')

const labelText = computed(() => {
  return mode.value === 'normal' ? 'Uso:' : 'Franquia:'
})

const target_desc = computed(() => {
  //console.log(props.module)
  switch (props.module) {

    case ModulosEnum.EMISSAO_COBRANCAS_BOLETO:
      return 'Emissão de Cobranças Via Boleto'
    case ModulosEnum.EMISSAO_COBRANCAS_CARTAO_CREDITO:
      return 'Emissão de Cobranças Via Cartão de Crédito'
    case ModulosEnum.EMISSAO_COBRANCAS_CARTAO_DEBITO:
      return 'Emissão de Cobranças Via Cartão de Débito'
    case ModulosEnum.EMISSAO_COBRANCAS_PIX:
      return 'Emissão de Cobranças Via Pix'
    case ModulosEnum.EMISSAO_LINK_PAGAMENTO:
      return 'Emissão de Cobranças Via Link de Pagamento'

    case ModulosEnum.SERASA_CONSULTA_BASICA:
      return 'Consulta Serasa Básica'
    case ModulosEnum.SERASA_CONSULTA_AVANCADA:
      return 'Consulta Serasa Avançada'
    case ModulosEnum.SERASA_NEGATIVACAO:
      return 'Negativação Serasa'
    case ModulosEnum.NOTAS_FISCAIS:
      return 'Emissão de NFS-e'
    case ModulosEnum.HIGIENIZACAO:
      return 'Higienização de Cadastro'
    default:
      return ''
  }
})

const percent = computed(() => {
  if (!props.max || Number(props.max) <= 0) return 0
  return Math.min(100, Math.round((props.counter / props.max) * 100))
})

const regra_colorizacao_progress_bar = computed(() => {
  if (percent.value <= 50) {
    return 'campaign-progress-low'
  } else if (percent.value > 50 && percent.value <= 70) {
    return 'campaign-progress-mid'
  }

  return 'campaign-progress-high'
})

const progressTrackClass = computed(() => {
  if (mode.value !== 'normal') {
    return ''
  }

  return 'ml-2 w-24 h-1.5 rounded-full overflow-hidden campaign-track'
})

const progressFillClass = computed(() => {
  if (percent.value <= 50) {
    return 'campaign-bar-low'
  } else if (percent.value > 50 && percent.value <= 70) {
    return 'campaign-bar-mid'
  }

  return 'campaign-bar-high'
})

const helpIconClass = computed(() => 'campaign-help-icon')

const displayUsed = computed(() => props.counter ?? 0)
const displayLimit = computed(() => props.max ?? 0)

const show = true
</script>

<style scoped lang="scss">
.campaign-badge {
  border: 1px solid #ebc6a0;
  background:
    radial-gradient(circle at 90% 15%, #e9944a2e 0, transparent 48%),
    linear-gradient(130deg, #fffaf3 0%, #fff0df 52%, #fff9f1 100%);
  margin-top: 0.45rem;
  box-shadow:
    inset 0 1px 0 #ffffffd6,
    0 4px 10px #a9652816;
}

.campaign-badge--normal {
  height: 1.6rem;
  padding: 0.25rem 0.65rem;
  border-radius: 9999px;
}

.campaign-badge--minimalista {
  height: 1.6rem;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
}

.campaign-badge--avancado {
  border: none;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.campaign-label {
  color: #a06e3d;
}

.campaign-track {
  background-color: #f7e2cd;
}

.campaign-progress-low {
  color: #aa7443;
}

.campaign-progress-mid {
  color: #c56f2d;
}

.campaign-progress-high {
  color: #b7522a;
}

.campaign-bar-low {
  background-color: #d7a173;
}

.campaign-bar-mid {
  background-color: #e67f24;
}

.campaign-bar-high {
  background-color: #d55e2b;
}

.campaign-help-icon {
  color: #bf6c2f !important;
}

:deep(.info-icon-button.p-button) {
  transition: none !important;
}

:deep(.info-icon-button.p-button:hover),
:deep(.info-icon-button.p-button:focus),
:deep(.info-icon-button.p-button:active),
:deep(.info-icon-button.p-button:focus-visible) {
  background: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  transform: none !important;
  opacity: 1 !important;
}
</style>

